import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal Web Speech API typings. The standard lib.dom.d.ts ships these in
// recent TypeScript releases but only behind specific lib targets, so we
// declare what we use here for portability.

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResult {
  readonly length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionLike;
}

interface BrowserGlobals {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
}

function getRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const w = window as unknown as BrowserGlobals;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface TranscriptionState {
  /** Final committed transcript text (newline-separated utterances). */
  transcript: string;
  /** Latest interim (in-progress) text. Reset on each final commit. */
  interim: string;
  /** True between start() and stop(). */
  isListening: boolean;
  /** True if the browser exposes a SpeechRecognition implementation. */
  supported: boolean;
  /** Last error code from the API, if any. */
  error: string | null;
  /** True when the operator deliberately pressed stop (vs an auto silence-stop or
   *  a recognition give-up). Lets the hearing backstop avoid auto-restarting
   *  listening after a deliberate stop. */
  userStopped: boolean;
  start: () => void;
  stop: () => void;
  /** Replace the whole final transcript (used by the Clear action and
   *  by hydration from the server on mount). */
  reset: (initial?: string) => void;
}

interface UseTranscriptionOptions {
  /** Initial committed transcript (e.g. server-side restore). */
  initial?: string;
  /** Locale tag, default en-AU. */
  lang?: string;
}

/**
 * Wraps SpeechRecognition with continuous mode and auto-restart. Chrome and
 * Edge cap a single recognition session at ~60 seconds of silence; we
 * transparently restart whenever onend fires while isListening is still true,
 * so the operator can leave it on for the entire hearing.
 */
export function useTranscription(options: UseTranscriptionOptions = {}): TranscriptionState {
  const { initial = '', lang = 'en-AU' } = options;

  const [transcript, setTranscript] = useState<string>(initial);
  const [interim, setInterim] = useState<string>('');
  const [isListening, setIsListening] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [userStopped, setUserStopped] = useState<boolean>(false);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const isListeningRef = useRef<boolean>(false);
  const restartTimerRef = useRef<number | null>(null);
  const Ctor = getRecognitionConstructor();
  const supported = Ctor !== null;

  // Build the recognition object once. Re-create only if the locale changes.
  useEffect(() => {
    if (!Ctor) {
      return;
    }
    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let finalChunk = '';
      let interimChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (!result || result.length === 0) {
          continue;
        }
        const alt = result[0];
        if (!alt) {
          continue;
        }
        if (result.isFinal) {
          finalChunk += alt.transcript;
        } else {
          interimChunk += alt.transcript;
        }
      }
      if (finalChunk) {
        setTranscript((prev) => appendUtterance(prev, finalChunk));
        setInterim('');
      } else if (interimChunk) {
        setInterim(interimChunk.trim());
      }
    };

    recognition.onerror = (event) => {
      // 'no-speech' is normal during long silences. 'aborted' fires on stop().
      if (event.error === 'no-speech' || event.error === 'aborted') {
        return;
      }
      setError(event.error);
    };

    recognition.onend = () => {
      // Chrome and Edge auto-stop a recognition session after ~60s of silence.
      // Restart transparently while the operator still wants to listen. A short
      // delay avoids the "recognition has already started" throw that can fire
      // when start() runs synchronously inside onend; if the first attempt still
      // throws, try once more before softly giving up.
      if (!isListeningRef.current) {
        setInterim('');
        return;
      }
      const tryRestart = (retry: boolean): void => {
        if (!isListeningRef.current) {
          return;
        }
        try {
          recognition.start();
        } catch {
          if (retry) {
            restartTimerRef.current = window.setTimeout(() => tryRestart(false), 400);
          } else {
            isListeningRef.current = false;
            setIsListening(false);
          }
        }
      };
      restartTimerRef.current = window.setTimeout(() => tryRestart(true), 150);
    };

    recognition.onstart = () => {
      setError(null);
    };

    recognitionRef.current = recognition;

    return () => {
      isListeningRef.current = false;
      if (restartTimerRef.current !== null) {
        window.clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.onstart = null;
      try {
        recognition.abort();
      } catch {
        // Ignore: abort throws if the recognition was never started.
      }
      recognitionRef.current = null;
    };
  }, [Ctor, lang]);

  const start = useCallback(() => {
    if (!recognitionRef.current || isListeningRef.current) {
      return;
    }
    try {
      recognitionRef.current.start();
      isListeningRef.current = true;
      setIsListening(true);
      setError(null);
      setUserStopped(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start microphone');
    }
  }, []);

  const stop = useCallback(() => {
    if (!recognitionRef.current) {
      return;
    }
    isListeningRef.current = false;
    setIsListening(false);
    setUserStopped(true);
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    try {
      recognitionRef.current.stop();
    } catch {
      // Ignore: stop throws if not started.
    }
    setInterim('');
  }, []);

  const reset = useCallback((next: string = '') => {
    setTranscript(next);
    setInterim('');
  }, []);

  return { transcript, interim, isListening, supported, error, userStopped, start, stop, reset };
}

function appendUtterance(existing: string, addition: string): string {
  const cleaned = addition.trim();
  if (!cleaned) {
    return existing;
  }
  if (existing.length === 0) {
    return cleaned;
  }
  return existing.endsWith('\n') ? `${existing}${cleaned}` : `${existing}\n${cleaned}`;
}
